// Pure helpers for the Relationship intelligence drawer: sort API findings
// into the three tables of the design (cross-document citations,
// within-document references, semantic relationships) and derive the counts
// every banner, tile and footer reads.
import { toTitleCaseFromSnake } from "../../utils";
import type {
  CitationRow,
  CitedDoc,
  InternalRefRow,
  RelationshipDetectionRun,
  RelationshipFinding,
  RelationshipResult,
  SemanticRow,
} from "./types";
import { formatDocType } from "../../utils";
import {
  CONFIDENCE_MODERATE_PCT,
  CONFIDENCE_STRONG_PCT,
  CROSS_DOCUMENT_RELATIONSHIP_TYPES,
  CROSS_DOCUMENT_TARGET_TYPES,
  REL_COLOR,
  RELATIONSHIP_DOC_FALLBACK_LABEL,
  RELATIONSHIP_LAYER_STATUS_LABEL,
  RELATIONSHIP_TYPE_HEX,
  RELATIONSHIP_TYPE_HEX_FALLBACK,
  RELATIONSHIP_TYPE_LABEL,
  SEMANTIC_RELATIONSHIP_TYPES,
  STATUS_RESOLVED,
  STATUS_UNRESOLVED,
  SYMMETRIC_RELATIONSHIP_TYPES,
  UNNAMED_TARGET,
  type RelBannerTone,
  type RelTagTone,
} from "./constants";

/* ── Small formatters ────────────────────────────────────────────── */

const upper = (v: unknown) => String(v ?? "").trim().toUpperCase();
const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown) => (typeof v === "number" && !Number.isNaN(v) ? v : null);

export function typeHex(type: string | null | undefined): string {
  return RELATIONSHIP_TYPE_HEX[upper(type)] ?? RELATIONSHIP_TYPE_HEX_FALLBACK;
}

export function typeLabel(type: string | null | undefined): string {
  return RELATIONSHIP_TYPE_LABEL[upper(type)] ?? toTitleCaseFromSnake(type);
}

export function pluralSuffix(n: number, suffix = "s"): string {
  return n === 1 ? "" : suffix;
}

export function pct(v: number | null | undefined): number {
  return typeof v === "number" ? Math.round(v * 100) : 0;
}

/** Mock confidence colour: >=STRONG green, >=MODERATE amber, else red. */
export function confidenceHex(v: number | null | undefined): string {
  const p = pct(v);
  if (p >= CONFIDENCE_STRONG_PCT) return REL_COLOR.green;
  if (p >= CONFIDENCE_MODERATE_PCT) return REL_COLOR.amber;
  return REL_COLOR.red;
}

export function statusOf(row: RelationshipFinding): string {
  if (row.status) return upper(row.status);
  if (row.resolved === true) return STATUS_RESOLVED;
  if (row.resolved === false) return STATUS_UNRESOLVED;
  return STATUS_UNRESOLVED;
}

/** "§2-baseline-governance¶3:29" -> "2". */
function sectionFromAnchor(id: string | null | undefined): string {
  const match = String(id ?? "").match(/^§?([^-¶\s]+)-/);
  return match ? match[1] : "";
}

function anchorLabel(id: string | null | undefined, fallbackSection?: string): string {
  const section = sectionFromAnchor(id) || text(fallbackSection);
  if (section) return `§${section}`;
  return text(id) || "-";
}

/* ── Classification ──────────────────────────────────────────────── */

type Bucket = "semantic" | "cross" | "internal";

function bucketOf(row: RelationshipFinding): Bucket {
  const rel = upper(row.relationship_type);
  if (SEMANTIC_RELATIONSHIP_TYPES.has(rel)) return "semantic";
  const refType = upper(row.reference_type ?? row.target_type);
  if (CROSS_DOCUMENT_RELATIONSHIP_TYPES.has(rel) || CROSS_DOCUMENT_TARGET_TYPES.has(refType)) {
    return "cross";
  }
  return "internal";
}

function toCitation(row: RelationshipFinding): CitationRow {
  const target = text(row.target_name) || text(row.target_title) || text(row.target_id);
  return {
    status: statusOf(row),
    type: upper(row.reference_type ?? row.target_type) || "OTHER",
    evidence: text(row.evidence),
    target: target || UNNAMED_TARGET,
    code: text(row.code),
    section: text(row.section),
    page: num(row.page),
    confidence: num(row.confidence),
    method: text(row.resolution_method) || text(row.detection_method) || text(row.method),
    reason: text(row.reason),
  };
}

function toInternalRef(row: RelationshipFinding): InternalRefRow {
  return {
    status: statusOf(row),
    type: upper(row.target_type) || "SECTION",
    relationshipType: upper(row.relationship_type),
    evidence: text(row.evidence),
    reason: text(row.reason),
    target: text(row.target_id) || UNNAMED_TARGET,
    section: text(row.section) || sectionFromAnchor(row.source_id),
    method: text(row.detection_method) || text(row.method),
    confidence: num(row.confidence),
  };
}

function toSemantic(row: RelationshipFinding): SemanticRow {
  const rel = upper(row.relationship_type);
  return {
    rel,
    symmetric: row.symmetric ?? SYMMETRIC_RELATIONSHIP_TYPES.has(rel),
    src: anchorLabel(row.source_id, row.section),
    tgt: anchorLabel(row.target_requirement_id ?? row.target_id),
    srcEvidence: text(row.source_evidence) || text(row.a_evidence) || text(row.evidence),
    tgtEvidence: text(row.target_evidence) || text(row.b_evidence),
    why: text(row.reason) || text(row.rationale),
    confidence: num(row.confidence),
    similarity: num(row.similarity),
  };
}

function roleCount(roles: unknown): number {
  if (Array.isArray(roles)) return roles.length;
  return typeof roles === "number" ? roles : 0;
}

function runText(value: RelationshipDetectionRun | string | undefined): string {
  if (typeof value === "string") return value.trim() || "not run";
  if (value && typeof value === "object") {
    return `completed, ${value.stored ?? 0} placed`;
  }
  return "not run";
}

/** Every derived value the drawer renders, read from the API. */
export function buildRelationshipView(data: RelationshipResult) {
  const findings = data.findings ?? [];
  const metrics = data.metrics ?? {};
  const cross: CitationRow[] = [];
  const internal: InternalRefRow[] = [];
  const semantic: SemanticRow[] = [];

  findings.forEach((row) => {
    const bucket = bucketOf(row);
    if (bucket === "semantic") semantic.push(toSemantic(row));
    else if (bucket === "cross") cross.push(toCitation(row));
    else internal.push(toInternalRef(row));
  });

  // Contradictions first, then by confidence: what a reviewer acts on.
  semantic.sort(
    (a, b) =>
      Number(b.rel === "CONTRADICTS") - Number(a.rel === "CONTRADICTS") ||
      (b.confidence ?? 0) - (a.confidence ?? 0),
  );

  const contradictions = Math.max(
    semantic.filter((r) => r.rel === "CONTRADICTS").length,
    metrics.contradictions ?? 0,
  );
  const duplicates = Math.max(
    semantic.filter((r) => r.rel === "DUPLICATES").length,
    metrics.duplicate_requirements ?? 0,
  );
  const roles = roleCount(metrics.roles);
  const detection = metrics.reference_detection;
  const chunks =
    detection && typeof detection === "object" ? num(detection.chunks_total) : null;

  return {
    cross,
    internal,
    semantic,
    citedDocs: groupCitedDocs(cross),
    contradictions,
    duplicates,
    roles,
    edges: data.count ?? cross.length + internal.length + semantic.length + roles,
    requirements: metrics.requirements_seen ?? 0,
    chunks,
    resolution: runText(metrics.reference_resolution),
    unresolvedCitations: cross.filter((r) => r.status !== STATUS_RESOLVED).length,
    methods: Array.from(new Set(internal.map((r) => r.method).filter(Boolean))),
  };
}

export type RelationshipView = ReturnType<typeof buildRelationshipView>;

/** One entry per cited document, with every citation that points at it. */
export function groupCitedDocs(rows: CitationRow[]): CitedDoc[] {
  const groups = new Map<string, CitedDoc>();
  rows.forEach((row) => {
    const key = `${row.target}|${row.target === UNNAMED_TARGET ? "" : row.type}`;
    const existing = groups.get(key);
    const evidence = {
      text: row.evidence,
      page: row.page,
      section: row.section,
      confidence: row.confidence,
    };
    if (existing) {
      existing.evidence.push(evidence);
      existing.resolved = existing.resolved || row.status === STATUS_RESOLVED;
      return;
    }
    groups.set(key, {
      key,
      name: row.target,
      code: row.code || (row.section && /[A-Z]{2,}-/.test(row.section) ? row.section : ""),
      type: row.type,
      resolved: row.status === STATUS_RESOLVED,
      evidence: [evidence],
    });
  });
  // Named, resolved documents first; the unnamed bucket last.
  return Array.from(groups.values()).sort(
    (a, b) =>
      Number(a.name === UNNAMED_TARGET) - Number(b.name === UNNAMED_TARGET) ||
      Number(b.resolved) - Number(a.resolved) ||
      a.name.localeCompare(b.name),
  );
}

/** Counts for a filter-pill row, in first-seen order. */
export function countBy<T>(rows: T[], pick: (row: T) => string): [string, number][] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = pick(row);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries());
}

export function matchesQuery(query: string, ...fields: (string | null | undefined)[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => String(f ?? "").toLowerCase().includes(q));
}

export function formatEvaluatedDate(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : "-";
}

/** Human-facing kind (Policy / Standard / Guideline / …). */
export function resolveDocKind(
  documentType: string | undefined,
  fallback: string | undefined,
): string {
  const raw = (documentType || fallback || "").toUpperCase();
  return raw ? formatDocType(raw) : RELATIONSHIP_DOC_FALLBACK_LABEL;
}

/** Layer status label, defaulting to "Complete" (as the API sometimes returns
 *  a lower-case value). Overridden to "Partial" when the drawer has flagged
 *  contradictions — the caller supplies that flag. */
export function resolveLayerStatusLabel(
  apiStatus: string | undefined,
  hasConflicts: boolean,
): string {
  if (hasConflicts) return RELATIONSHIP_LAYER_STATUS_LABEL.partial;
  const title = toTitleCaseFromSnake(apiStatus || "complete");
  return title || RELATIONSHIP_LAYER_STATUS_LABEL.complete;
}

/** Prompt-version / status label for the `Semantic Relationships` right-slot. */
export function resolveRelDetectionLabel(
  detection: RelationshipMetrics["relationship_detection"],
): string {
  if (typeof detection === "string") {
    return `relationship detection ${detection}`;
  }
  return detection?.prompt_version ?? "";
}

type RelationshipMetrics = NonNullable<RelationshipResult["metrics"]>;

export interface RelationshipHeaderState {
  view: RelationshipView;
  nCit: number;
  nInt: number;
  nSem: number;
  title: string | undefined;
  kind: string;
  kindLower: string;
  layerStatus: string;
  hasConflicts: boolean;
  allUnresolved: boolean;
  mainTone: RelBannerTone;
  headerStatusTone: RelTagTone;
  headerTags: { label: string; tone?: RelTagTone }[];
  relDetectionLabel: string;
}

/** One-shot derivation of everything `RelationshipContent` renders in the
 *  header, banners and footer.  Keeping this in a helper means the drawer
 *  itself just spreads the derived object into JSX — no scattered locals. */
export function buildRelationshipHeaderState(
  data: RelationshipResult,
  documentTitle: string | undefined,
  documentType: string | undefined,
): RelationshipHeaderState {
  const view = buildRelationshipView(data);
  const nCit = view.cross.length;
  const nInt = view.internal.length;
  const nSem = view.semantic.length;
  const hasConflicts = view.contradictions > 0;
  const allUnresolved = nCit > 0 && view.unresolvedCitations === nCit;
  const kind = resolveDocKind(documentType, data.document?.document_type);
  const layerStatus = resolveLayerStatusLabel(data.status, hasConflicts);
  const mainTone: RelBannerTone =
    hasConflicts || view.unresolvedCitations > 0 ? "amber" : "green";
  const headerStatusTone: RelTagTone =
    hasConflicts ||
    layerStatus.toLowerCase() !== RELATIONSHIP_LAYER_STATUS_LABEL.complete.toLowerCase()
      ? "amber"
      : "green";

  return {
    view,
    nCit,
    nInt,
    nSem,
    title: documentTitle || data.document?.title,
    kind,
    kindLower: kind.toLowerCase(),
    layerStatus,
    hasConflicts,
    allUnresolved,
    mainTone,
    headerStatusTone,
    headerTags: [{ label: kind, tone: "accent" }],
    relDetectionLabel: resolveRelDetectionLabel(data.metrics?.relationship_detection),
  };
}

/** First few words of the title, one per line, for the graph's centre node. */
export function hubLabelLines(title: string | undefined): string[] {
  const words = String(title ?? "")
    .replace(/\bv?\d+(\.\d+)*\b/gi, "")
    .split(/\s+/)
    .filter(Boolean);
  const lines = words.slice(0, 3);
  return lines.length ? lines : ["This", "document"];
}

/** Wrap a label onto at most `maxLines` lines without truncating any word. */
export function wrapLabel(text: string | undefined, maxChars = 20, maxLines = 2): string[] {
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  words.forEach((word) => {
    const next = cur ? `${cur} ${word}` : word;
    if (!cur || next.length <= maxChars || lines.length === maxLines - 1) {
      cur = next;
      return;
    }
    lines.push(cur);
    cur = word;
  });
  if (cur) lines.push(cur);
  return lines;
}
